import { useCallback, useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import dataProvider from "server/dataProvider";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import { doctoralRequests } from "api/doctoralApi";
import { employeeRequests } from "api/employeeApi";
import { administrationRequests } from "api/administrationApi";
import { DoctorantDefenseResultChoices, DoctorantDefenseResultColorMap } from "utils/choices";
import { DoctorantStatus } from "utils/constants";
import StatusChip from "../../Partials/StatusChip";
import DefenseJuryDialog from "./DefenseJuryDialog";
import { formatDoctoralDate } from "../../Partials/DoctoralDateField";
import ScheduleDefenseDialog from "./ScheduleDefenseDialog";
import RecordDefenseResultControl from "./RecordDefenseResultControl";

const formatDateTime = iso => formatDoctoralDate(iso, true) || "—";

const memberRoles = member =>
  [member.isChairman ? "Председател" : null, member.isReviewer ? "Рецензент" : null].filter(Boolean).join(", ");

const DefenseSection = () => {
  const record = useRecordContext();
  const doctorantId = record?.id;

  const [defense, setDefense] = useState(null);
  const [memberLabels, setMemberLabels] = useState({});
  const [roomLabel, setRoomLabel] = useState("");

  const reload = useCallback(async () => {
    if (!doctorantId) return;

    let defenseData = null;
    try {
      const { data } = await dataProvider.customRequest(doctoralRequests.getDoctorantDefense(doctorantId), null, "GET");
      defenseData = data ?? null;
    } catch (err) {
      console.error("Неуспешно зареждане на защитата", err);
    }
    setDefense(defenseData);

    const employeeIds = (defenseData?.juryMembers ?? []).map(m => m.employeeId).filter(Boolean);
    if (employeeIds.length > 0) {
      try {
        const employees = await dataProvider.getMany(employeeRequests.getEmployeesWithDegreeSelect(), {
          ids: employeeIds,
        });
        const map = {};
        (employees.data || []).forEach(e => {
          map[e.id] = getProfessorRecordText(e);
        });
        setMemberLabels(map);
      } catch (err) {
        console.error("Неуспешно зареждане на членовете на журито", err);
        setMemberLabels({});
      }
    } else {
      setMemberLabels({});
    }

    if (defenseData?.roomId) {
      try {
        const rooms = await dataProvider.getMany(administrationRequests.getRoomsSelect(), {
          ids: [defenseData.roomId],
        });
        const room = (rooms.data || [])[0];
        setRoomLabel(room ? [room.roomName, room.buildingName].filter(Boolean).join(" - ") : "");
      } catch (err) {
        console.error("Неуспешно зареждане на залата", err);
        setRoomLabel("");
      }
    } else {
      setRoomLabel("");
    }
  }, [doctorantId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const hasJury = (defense?.juryMembers?.length ?? 0) > 0;
  const currentClosed = Boolean(defense?.result);
  const isTerminal = record?.status === DoctorantStatus.DefendedSuccessfully;
  const pastAttempts = (defense?.history ?? []).filter(a => a.id !== defense?.id);

  return (
    <Box>
      <SecuredComponent permission={permissions.canDismissDoctorants}>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap", mb: 2 }}>
          {!isTerminal && (
            <DefenseJuryDialog
              defense={currentClosed ? null : defense}
              startNewAttempt={currentClosed}
              onSaved={reload}
            />
          )}
          {hasJury && !currentClosed && (
            <ScheduleDefenseDialog
              defense={defense}
              onSaved={reload}
            />
          )}
        </Box>
      </SecuredComponent>

      {!hasJury ? (
        <em>Няма назначено жури</em>
      ) : (
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <Typography
              variant="caption"
              color="primary"
            >
              Изпитно жури
            </Typography>
            {defense.juryMembers.map(m => (
              <Typography key={m.id}>
                {m.employeeId
                  ? memberLabels[m.employeeId] || `Служител №${m.employeeId}`
                  : `${m.fullName} (${m.university})`}
                {memberRoles(m) ? ` — ${memberRoles(m)}` : ""}
              </Typography>
            ))}
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              variant="caption"
              color="primary"
              display="block"
            >
              Дата и час на защита
            </Typography>
            <Typography>{formatDateTime(defense.defenseDate)}</Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              variant="caption"
              color="primary"
              display="block"
            >
              Зала
            </Typography>
            <Typography>{roomLabel || "—"}</Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              variant="caption"
              color="primary"
              display="block"
            >
              Резултат
            </Typography>
            {defense.result ? (
              <StatusChip
                status={defense.result}
                choices={DoctorantDefenseResultChoices}
                colorMap={DoctorantDefenseResultColorMap}
              />
            ) : (
              <Typography>—</Typography>
            )}
          </Grid>

          {defense.canRecordResult && (
            <Grid
              item
              xs={12}
            >
              <RecordDefenseResultControl onSaved={reload} />
            </Grid>
          )}

          {pastAttempts.length > 0 && (
            <Grid
              item
              xs={12}
            >
              <Typography
                variant="caption"
                color="primary"
                display="block"
              >
                Предходни опити
              </Typography>
              {pastAttempts.map(attempt => (
                <Box
                  key={attempt.id}
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                >
                  <Typography>{formatDateTime(attempt.defenseDate)}</Typography>
                  {attempt.result ? (
                    <StatusChip
                      status={attempt.result}
                      choices={DoctorantDefenseResultChoices}
                      colorMap={DoctorantDefenseResultColorMap}
                    />
                  ) : (
                    <Typography>—</Typography>
                  )}
                </Box>
              ))}
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default DefenseSection;
